import { Error, Paging } from "api/baseApiResponseModel/BaseApiResponseModel";
import { CarrierListRequestModel } from "api/merchantRepository/filterFeature/model/carrierList/CarrierListRequestModel";
import { CarrierListResponseModel } from "api/merchantRepository/filterFeature/model/carrierList/CarrierListResponseModel";
import { PackageListRequestModel } from "api/merchantRepository/filterFeature/model/packageList/PackageListRequestModel";
import { PackageListResponseModel } from "api/merchantRepository/filterFeature/model/packageList/PackageListResponseModel";
import { PackageRegistrationRequestModel } from "api/merchantRepository/packageRegistration/model/PackageRegistrationRequestModel";
import { PackageRegistrationResponseModel } from "api/merchantRepository/packageRegistration/model/PackageRegistrationResponseModel";
import { defaultStoreRepository } from "api/repositories/store/StoreRepository";
import { defaultUserManagementRepository } from "api/repositories/userManagement/UserManagementRepository";
import { BaseViewModel } from "components/base/BaseViewModel";
import dayjs from "dayjs";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { strings } from "utils/localizedStrings";
import { ServiceRepo } from "utils/serviceRepo/ServiceRepo";

class PackageRegistrationInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<PackageRegistrationRequestModel>(
    new PackageRegistrationRequestModel()
  );
  public fetchCarrier = new BehaviorSubject<CarrierListRequestModel>(
    new CarrierListRequestModel()
  );
  public fetchPackage = new BehaviorSubject<PackageListRequestModel>(
    new PackageListRequestModel()
  );
  public exportExcel = new BehaviorSubject<PackageRegistrationRequestModel>(
    new PackageRegistrationRequestModel()
  );
  public fetchDetail = new BehaviorSubject<PackageRegistrationRequestModel>(
    new PackageRegistrationRequestModel()
  );
  public checkStatus = new BehaviorSubject<string>("");
  public loadStores = new BehaviorSubject<{ userType: number }>({
    userType: 0,
  });
  public loadStaff = new BehaviorSubject<{
    selectedStore: Array<string>;
    userType: number;
  }>({ selectedStore: [], userType: 0 });

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchList.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.fetchPackage.complete();
    this.checkStatus.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
  }
}

class PackageRegistrationOuput implements Disposable {
  public savedList = new BehaviorSubject<
    Array<PackageRegistrationResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<CarrierListResponseModel>>(
    []
  );
  public savedPackage = new BehaviorSubject<Array<PackageListResponseModel>>(
    []
  );
  public savedDetail = new BehaviorSubject<PackageRegistrationResponseModel>(
    {}
  );
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public storeOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);
  public staffOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedPackage.complete();
    this.pagination.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.storeOptions.complete();
    this.staffOptions.complete();
  }
}

@injectable()
export class PackageRegistrationViewModel extends BaseViewModel<
  PackageRegistrationInput,
  PackageRegistrationOuput
> {
  constructor() {
    super(new PackageRegistrationInput(), new PackageRegistrationOuput());
  }
  private repo = ServiceRepo.getPackageRegistrationRepo();
  private filterRepo = ServiceRepo.getFilterFeatureRepo();

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.repo.getList(params);
        if (res?.data) {
          this.output.savedList.next(res?.data ?? []);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.savedList.next([]);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchPackage, async (params) => {
      const cache = productCache?.getCache("mobilePlanPackage");
      if (cache?.length) {
        this.output.savedPackage.next(cache);
        return;
      }
      try {
        const res = await this.filterRepo.getPackageList(params);
        if (res?.data) {
          this.output.savedPackage.next(res?.data);
          productCache?.saveCache(res?.data, "mobilePlanPackage");
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.filterRepo.getCarrierList(params);
        if (res?.data) {
          const finalData = res?.data?.filter(
            (child) =>
              child?.code === "viettel" ||
              child?.code === "mobifone" ||
              child?.code === "vinaphone"
          );
          this.output.savedCarrier.next(finalData);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.detailLoading.next(true);
        const res = await this.repo.getDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.detailLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.repo.exportReport(params);
        if (res?.data) {
          const url = window.URL.createObjectURL(res?.data as Blob);
          const link = document.createElement("a");
          const timestamp = dayjs().format("YYYYMMDD_HHmmss");
          link.href = url;
          link.setAttribute("download", `MOBILE_PLAN_REPORT_${timestamp}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.checkStatus, async (code) => {
      if (!code) return;
      try {
        this.input.statusLoading.next({ [code]: true });
        const res = await this.repo.checkStatus(code);
        if (res?.data) {
          this.output.onCheckStatusSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.statusLoading.next({ [code]: false });
      }
    });

    this.skipFirstInitial(this.input.loadStores, async ({ userType }) => {
      try {
        const cache = productCache.getCache("storeList");
        if (userType === 3) {
          if (!cache?.length) {
            const res = await defaultStoreRepository.getStoreList({
              page: 0,
              limit: 500,
              status: 0,
            } as any);
            const items = res?.data?.data?.items || [];
            const opts = [
              { label: strings.GlobalLabels.All, value: "" },
              ...items.map((s) => ({
                label: s?.name || s?.code,
                value: s?.userId,
                code: s?.code,
              })),
            ];
            productCache.saveCache(
              opts as unknown as Record<string, unknown>,
              "storeList"
            );
            this.output.storeOptions.next(opts);
          } else {
            this.output.storeOptions.next(cache);
          }
        }
      } catch (e) {
        this.output.storeOptions.next([
          { label: strings.GlobalLabels.All, value: "" },
        ]);
      }
    });

    this.skipFirstInitial(
      this.input.loadStaff,
      async ({ selectedStore, userType }) => {
        const isAgency = userType === 3 && selectedStore?.length > 0;
        const isStore = userType === 4;

        if (userType === 3 && !selectedStore?.length) {
          this.output.staffOptions.next([
            { label: strings.GlobalMessage.InvalidStore, value: "" },
          ]);
          return;
        }

        try {
          if (isAgency || isStore) {
            const res = await defaultUserManagementRepository.getUserListV2({
              page: 0,
              limit: 500,
              status: 0,
              ...(selectedStore ? { store: selectedStore } : {}),
            } as any);
            const items: any[] = res?.data?.items || [];
            const opts = [
              { label: strings.GlobalLabels.All, value: "" },
              ...items.map((u: any) => ({
                label: u?.fullName || u?.userName || u?.phone,
                value: u?.id,
                type: u?.type,
              })),
            ];
            this.output.staffOptions.next(opts);
          }
        } catch (e) {
          this.output.staffOptions.next([
            { label: strings.GlobalLabels.All, value: "" },
          ]);
        }
      }
    );
  }
}
